import { t } from '@lingui/macro'

import {
  CampaignActionCertificationModel,
  CampaignActionPathwayModel,
  CampaignActionSurveyModel,
  CampaignActionTopicModel,
  CampaignBannerModel,
  CampaignCertificationShareActionModelAsEditor,
  CampaignEmailActionModel,
  CampaignMessageActionModel,
  CampaignMessageTypeEnum,
  CampaignPathwayShareActionModel,
  CampaignSurveyShareActionModel,
  CampaignTopicShareActionModel
} from '../../graphql/private/PrivateSchemaTypes'
import { stripHTMLTags } from 'shared/HTML'
import { ICampaignAction } from 'front-end/src/pages/manage-campaign-page/campaign-actions/CampaignActions'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export const CampaignActionEditingSteps = {
  CREATE: 'Create',
  PREVIEW: 'Preview',
  SCHEDULE: 'Schedule'
}

export const campaignMessageTypes = [
  {
    value: CampaignMessageTypeEnum.SMS,
    label: 'SMS'
  },
  {
    value: CampaignMessageTypeEnum.SLACK,
    label: 'Slack'
  },
  {
    value: CampaignMessageTypeEnum.FOURTH,
    label: 'Fourth'
  },
  {
    value: CampaignMessageTypeEnum.MSTEAMS,
    label: 'MS Teams'
  }
]

export const campaignMessageTypeLabel = (type: string) =>
  campaignMessageTypes.find((it) => it.value === type)?.label ?? ''

export const getCampaignActionName = (
  action: ICampaignAction,
  actionType?: CampaignActionTypeEnum
) => {
  if (!actionType) {
    return ''
  }

  if (
    actionType === CampaignActionTypeEnum.EMAIL ||
    actionType === CampaignActionTypeEnum.MANAGER_NOTIFICATION
  ) {
    return (action as CampaignEmailActionModel).subject
  }

  if (actionType === CampaignActionTypeEnum.BANNER) {
    return (action as CampaignBannerModel).header
  }

  if (actionType === CampaignActionTypeEnum.TOPIC_SHARE_ACTION) {
    return getTopicShareActionName(
      (action as CampaignTopicShareActionModel).actionTopics
    )
  }

  if (actionType === CampaignActionTypeEnum.PATHWAY_SHARE_ACTION) {
    return getPathwayShareName(
      (action as CampaignPathwayShareActionModel).actionPathways
    )
  }

  if (actionType === CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION) {
    return getCertificationShareName(
      (action as CampaignCertificationShareActionModelAsEditor)
        .actionCertifications
    )
  }

  if (actionType === CampaignActionTypeEnum.SURVEY_SHARE_ACTION) {
    return getSurveyShareName(
      (action as CampaignSurveyShareActionModel).actionSurveys
    )
  }

  if (actionType === CampaignActionTypeEnum.MESSAGE) {
    if ((action as CampaignMessageActionModel).text) {
      return stripHTMLTags(
        (action as CampaignMessageActionModel).text
      ).substring(0, 10)
    }
    return ''
  }

  return ''
}

const getTopicShareActionName = (actionTopics: CampaignActionTopicModel[]) => {
  if (actionTopics.length === 1) {
    return t`share resource ${actionTopics[0].topic.currentTitle}`
  }

  return t`share ${actionTopics.length} resources`
}

const getPathwayShareName = (actionPathways: CampaignActionPathwayModel[]) => {
  if (actionPathways.length === 1) {
    return t`share pathway ${actionPathways[0].pathway.title}`
  }

  return t`share ${actionPathways.length} pathways`
}

const getSurveyShareName = (actionSurveys: CampaignActionSurveyModel[]) => {
  if (actionSurveys.length === 1) {
    return t`share survey ${actionSurveys[0].survey.title}`
  }

  return t`share ${actionSurveys.length} surveys`
}

const getCertificationShareName = (
  actionCertifications: CampaignActionCertificationModel[]
) => {
  if (actionCertifications.length === 1) {
    return t`share certification ${actionCertifications[0].certification.name}`
  }

  return t`share ${actionCertifications.length} certifications`
}
